import { decodeJwtPayload } from '@hetivo/hetivo-lib-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import type { HetivoAuthState } from './hetivo-auth-context';
import { SESSION_ENDED_REASON } from './session-ended-reason';
import type { SessionEndedReason } from './session-ended-reason';

// --- Constants ---

const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

// --- Types ---

interface ImpersonationRefs {
  inactivityTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  hetivoTokenRef: React.MutableRefObject<string | null>;
  sessionTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  tokenExpiryRef: React.MutableRefObject<number | null>;
}

export interface UseImpersonationSessionResult {
  /** Clear the impersonation session (used during sign-out) */
  clearImpersonation: () => void;
  /** Initial auth state computed synchronously from the token */
  initialState: HetivoAuthState;
  /** Whether we're currently in impersonation mode */
  isImpersonating: boolean;
  /** The active impersonation token (null if not impersonating) */
  token: string | null;
}

// --- Helpers ---

/**
 * Compute the initial auth state synchronously.
 *
 * When an impersonation token is provided we decode it immediately and start
 * in an authenticated state. This is critical because TanStack Router's
 * `beforeLoad` guard runs synchronously on the very first render — if the
 * initial state were `{ isAuthenticated: false, isLoading: true }`, the guard
 * would redirect to `/login` before any `useEffect` has a chance to process
 * the token.
 */
export function buildInitialState(impersonationToken?: string | null): HetivoAuthState {
  const baseState: HetivoAuthState = {
    error: null,
    isAuthenticated: false,
    isImpersonating: false,
    isLoading: false,
    hetivoToken: null,
    sessionEndedReason: null,
    user: null,
  };

  if (!impersonationToken) {
    return { ...baseState, isLoading: true };
  }

  const payload = decodeJwtPayload(impersonationToken);
  if (!payload) {
    return { ...baseState, error: 'Invalid impersonation token' };
  }

  // Reject already-expired tokens synchronously so route guards never see
  // a brief authenticated state before the effect cleans up.
  const exp = payload.exp as number | undefined;
  if (exp && exp * 1000 <= Date.now()) {
    return { ...baseState, sessionEndedReason: SESSION_ENDED_REASON.MAX_DURATION };
  }

  return {
    ...baseState,
    isAuthenticated: true,
    isImpersonating: true,
    hetivoToken: impersonationToken,
    user: {
      email: (payload.login as string) ?? '',
      name: (payload.login as string) ?? '',
    },
  };
}

// --- Hook ---

/**
 * Encapsulates all impersonation-specific logic:
 * - Token state management (read once on mount via lazy initializer)
 * - Initial auth state computation
 * - JWT expiry timer setup
 * - Sign-out helper
 * - `onSessionEnd` (optional): invoked when the JWT exp timer ends the session, so the parent can
 *   run its sign-out cleanup (the Firebase listener, which normally does, is skipped here)
 *
 * The hook accepts a `setStateRef` (ref to the parent's setState) so it can trigger
 * session expiry transitions when the JWT exp timer fires. The ref is populated by
 * the parent after its own `useState` call, and is always current before any effect runs.
 */
export function useImpersonationSession(
  readImpersonationToken: (() => string | null) | undefined,
  refs: ImpersonationRefs,
  setStateRef: React.MutableRefObject<React.Dispatch<React.SetStateAction<HetivoAuthState>>>,
  onSessionEnd?: () => void,
): UseImpersonationSessionResult {
  const { inactivityTimerRef, hetivoTokenRef, sessionTimerRef, tokenExpiryRef } = refs;

  // Read the token once synchronously on mount via lazy initializer.
  // Using state (not just a ref) ensures all dependent useEffect hooks re-run
  // when the token is cleared on sign-out, allowing the Firebase listener to re-register.
  const [token, setToken] = useState<string | null>(() => readImpersonationToken?.() ?? null);

  // Compute the initial auth state synchronously so TanStack Router's beforeLoad
  // guard sees the correct authenticated state on the very first render.
  // We use a ref to hold the initial state so it is only computed once.
  const initialStateRef = useRef<HetivoAuthState | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = buildInitialState(token);
  }

  const handleSessionEnd = useCallback(
    (reason: SessionEndedReason): void => {
      hetivoTokenRef.current = null;
      tokenExpiryRef.current = null;
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      setToken(null);
      // The Firebase listener is skipped while impersonating, so flush the app caches here.
      onSessionEnd?.();
      setStateRef.current({
        error: null,
        isAuthenticated: false,
        isImpersonating: false,
        isLoading: false,
        hetivoToken: null,
        sessionEndedReason: reason,
        user: null,
      });
    },
    [inactivityTimerRef, hetivoTokenRef, onSessionEnd, sessionTimerRef, setStateRef, tokenExpiryRef],
  );

  // Handle impersonation token side-effects (refs and expiry timer).
  // The auth *state* is already set synchronously by the useState initializer
  // (buildInitialState), so this effect only handles the mutable refs and timers.
  useEffect(() => {
    if (!token) return;

    const payload = decodeJwtPayload(token);
    if (!payload) {
      sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      setToken(null);
      return; // Invalid token — initial state already set the error
    }

    hetivoTokenRef.current = token;

    const exp = payload.exp as number | undefined;
    if (!exp) return;

    const expiryMs = exp * 1000;
    const remainingMs = expiryMs - Date.now();
    tokenExpiryRef.current = expiryMs;

    if (remainingMs <= 0) {
      handleSessionEnd(SESSION_ENDED_REASON.MAX_DURATION);
      return;
    }

    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);

    sessionTimerRef.current = setTimeout(() => {
      handleSessionEnd(SESSION_ENDED_REASON.MAX_DURATION);
    }, remainingMs);
  }, [handleSessionEnd, hetivoTokenRef, sessionTimerRef, token, tokenExpiryRef]);

  const clearImpersonation = useCallback((): void => {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    setToken(null);
  }, []);

  return {
    clearImpersonation,
    initialState: initialStateRef.current,
    isImpersonating: token !== null,
    token,
  };
}
